import { useCallback, useEffect, useMemo, useState } from 'react';

import { orderBy } from '@/utils/array-utils';
import tailwindConfig from '@/utils/tailwind-utils';

export enum Breakpoint {
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  xxl = '2xl',
}

export type BreakpointMap<T = number> = {
  [key in Breakpoint]: T;
};

export type PartialBreakpointMap<T = number> = Partial<BreakpointMap<T>>;

const parseTailwindBreakpoint = (breakpoint: string) =>
  parseInt(breakpoint, 10);

export const breakpointsPx: BreakpointMap<number> = {
  [Breakpoint.sm]: parseTailwindBreakpoint(tailwindConfig.screens.sm),
  [Breakpoint.md]: parseTailwindBreakpoint(tailwindConfig.screens.md),
  [Breakpoint.lg]: parseTailwindBreakpoint(tailwindConfig.screens.lg),
  [Breakpoint.xl]: parseTailwindBreakpoint(tailwindConfig.screens.xl),
  [Breakpoint.xxl]: parseTailwindBreakpoint(tailwindConfig.screens['2xl']),
};

export type UseBreakpointsProps<T> = {
  window: {
    width: number;
  };
  currentBreakpoint: Breakpoint;
  currentEntry: T;
};

const useBreakpoints = <T = number>(
  breakpoints: PartialBreakpointMap<T> = breakpointsPx as PartialBreakpointMap<T>
): UseBreakpointsProps<T> => {
  const [windowWidth, setWindowWidth] = useState<number>(0);

  const currentBreakpoint = useMemo((): Breakpoint => {
    const entries = orderBy(Object.entries(breakpoints), ([, value]) => value);

    if (entries.length === 0) {
      throw new Error('No breakpoints defined');
    }

    for (const [key] of entries) {
      const value = breakpointsPx[key as Breakpoint];
      if (windowWidth <= value) {
        return key as Breakpoint;
      }
    }

    return entries[entries.length - 1][0] as Breakpoint;
  }, [breakpoints, windowWidth]);

  const currentEntry = useMemo((): T => {
    return breakpoints[currentBreakpoint] as T;
  }, [breakpoints, currentBreakpoint]);

  const updateWindowWidth = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    window.addEventListener('resize', updateWindowWidth);

    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, [updateWindowWidth]);

  return {
    window: {
      width: windowWidth,
    },
    currentBreakpoint,
    currentEntry,
  };
};

export default useBreakpoints;
